import { useEffect } from 'react';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import { AnimatePresence, domAnimation, LazyMotion, MotionDiv } from '@/components/Motion';
import { EntityTypeEnum, QuestionTypeEnum } from '@/types/schema';

import { FeedbackQuestionFragment } from '../feedback/graphql/fragments/generated/FeedbackQuestions';
import useFeedbackActions from '../feedback/hooks/useFeedbackActions';
import FeedbackActions from './components/FeedbackActions';
import useAIFeedbackData from './hooks/useAIFeedbackData';
import { AIFeedbackProps } from './types';

const ANIMATION = {
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
  initial: { opacity: 0, height: 0 },
  transition: {
    opacity: { duration: 0.2 },
    layout: {
      type: 'spring',
      bounce: 0.4,
      duration: 0.2,
    },
  },
};

type AIFeedbackContentProps = AIFeedbackProps & { questions: FeedbackQuestionFragment[] };

const AIFeedbackContent: React.FC<AIFeedbackContentProps> = (props) => {
  const {
    justifyContent = 'between',
    className,
    questions,
    entityId,
    position,
    motionDivClassname,
    showOnlyIcon,
    buttonSize,
    gapX = 4,
    forceUpdate,
  } = props;
  const { t } = useTranslation();

  const {
    onEmojiClick,
    onSubmit,
    feedbackTaken,
    feedbackQuestionsObject,
    dialogOpen,
    onDialogClose,
    emojiResponse,
    hideAIFeedback,
  } = useFeedbackActions({
    questions,
    entityId,
    entityType: EntityTypeEnum.GenerativeTask,
  });

  useEffect(() => {
    forceUpdate?.();
  }, [forceUpdate, hideAIFeedback, feedbackTaken]);

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence initial mode="sync">
        {hideAIFeedback ? null : (
          <MotionDiv
            animate={ANIMATION.animate}
            className={cn('flex-1', motionDivClassname)}
            exit={ANIMATION.exit}
            initial={ANIMATION.initial}
            transition={ANIMATION.transition}>
            <Stack
              alignItems="center"
              className={cn('flex-1 rounded-8 bg-bg-secondary py-12 pe-10 ps-12', className)}
              gapX={24}
              justifyContent={justifyContent}>
              {showOnlyIcon ? null : (
                <Typography color="text-text-secondary" variant="body-short-02">
                  {feedbackTaken
                    ? t('Common:AIFeedback.FeedbackSubmitted')
                    : feedbackQuestionsObject.feedbackRating.questionText}
                </Typography>
              )}
              <Stack alignItems="center" gapX={gapX}>
                <FeedbackActions
                  buttonSize={buttonSize}
                  dialogOpen={dialogOpen}
                  disabled={feedbackTaken}
                  emojiResponse={emojiResponse}
                  position={position}
                  onClose={onDialogClose}
                  onIconClick={onEmojiClick}
                  onSubmit={onSubmit}
                />
              </Stack>
            </Stack>
          </MotionDiv>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
};

// to allow nullable feedback questions in props and handle properly the case when there are less than two questions
const AIFeedback = (props: AIFeedbackProps) => {
  const { fetching, feedbackQuestions } = useAIFeedbackData();

  if (fetching) {
    return null;
  }

  const questions =
    feedbackQuestions && feedbackQuestions[0]?.questions ? feedbackQuestions[0]?.questions : [];

  if (
    !questions ||
    questions.length < 2 ||
    questions[0].questionType !== QuestionTypeEnum.Emoji ||
    questions[1].questionType !== QuestionTypeEnum.Text
  ) {
    ErrorReporter.error(
      new Error(`[AIFeedback]: Incorrect questions data: ${JSON.stringify(questions)}`),
    );
    return null;
  }

  return <AIFeedbackContent {...props} questions={questions} />;
};

export default AIFeedback;
